/**
 * Admin API client for user management
 */

if (!import.meta.env.VITE_BACKEND_URL) {
  throw new Error('VITE_BACKEND_URL environment variable is required');
}
const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

export interface UserData {
  id: string;
  email: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  tier: string;
  created_at: string;
  session_count: number;
  last_session: string | null;
  profile_complete: boolean;
}

export interface PaginationMetadata {
  page: number;
  limit: number;
  total_users: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface PaginatedUsersResponse {
  users: UserData[];
  pagination: PaginationMetadata;
}

export interface RoleUpdateResponse {
  user_id: string;
  old_role: string;
  new_role: string;
  message: string;
}

/**
 * Get all users with pagination and search (admin only)
 */
export async function getAllUsers(
  page: number = 1,
  limit: number = 50,
  search?: string
): Promise<PaginatedUsersResponse> {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (search && search.trim()) {
    params.append('search', search.trim());
  }

  const response = await fetch(`${API_BASE_URL}/admin/users?${params.toString()}`, {
    method: 'GET',
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin privileges required');
    }
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get details for a specific user (admin only)
 */
export async function getUserDetails(userId: string): Promise<UserData> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin privileges required');
    }
    if (response.status === 404) {
      throw new Error('User not found');
    }
    throw new Error(`Failed to fetch user details: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a user's role (admin only, requires password confirmation)
 */
export async function updateUserRole(
  userId: string,
  newRole: string,
  password: string
): Promise<RoleUpdateResponse> {
  const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/role`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      role: newRole,
      password_confirmation: { password }
    }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Admin privileges required');
    }
    if (response.status === 400) {
      const error = await response.json();
      throw new Error(error.detail || 'Invalid request');
    }
    if (response.status === 404) {
      throw new Error('User not found');
    }
    throw new Error(`Failed to update user role: ${response.statusText}`);
  }

  return response.json();
}
